import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  InputLabel,
  LinearProgress,
  Skeleton,
  Typography,
} from "@mui/material";
import ConfirmationModal from "components/modals/confirmation-modal";
import { OverageField } from "components/settings/shared/overage/overage-field";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  cancelFutureSubscription,
  undoScheduledCancellation,
} from "data/mutations";
import {
  fetchCredits,
  fetchPaymentMethod,
  fetchSubscription,
} from "data/queries";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { toTitleCase } from "utils/commons";
import { formatInterval, formatPrice, humanizeNumber } from "utils/format";
import { resolveIdentityId } from "utils/identity";

export const Subscription = () => {
  const history = useHistory();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const subscriptionQuery = useQuery([QueryKeys.subscription, identityId], () =>
    fetchSubscription()
  );

  const creditsQuery = useQuery(
    [QueryKeys.credits, identityId],
    () => fetchCredits(),
    {
      enabled: !!subscriptionQuery.data?.activeSubscription, // only run when subscription exists
    }
  );

  const paymentMethodQuery = useQuery(
    [QueryKeys.paymentMethod, identityId],
    () => fetchPaymentMethod()
  );
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const futureSubscriptionMutation = useMutation(
    () => cancelFutureSubscription(),
    {
      onSuccess: () => {
        queryClient.setQueryData([QueryKeys.subscription, identityId], {
          ...subscriptionQuery.data,
          activeSubscription: {
            ...subscriptionQuery.data?.activeSubscription,
            nextSub: undefined,
          },
        });
        setCancelModalIsOpen(false);
        enqueueSnackbar(
          "You successfully cancelled your pending subscription downgrade.",
          {
            variant: "success",
          }
        );
      },
    }
  );

  const undoScheduledCancellationMutation = useMutation(
    () => undoScheduledCancellation(),
    {
      onSuccess: () => {
        queryClient.setQueryData([QueryKeys.subscription, identityId], {
          ...subscriptionQuery.data,
          activeSubscription: {
            ...subscriptionQuery.data?.activeSubscription,
            cancelAtPeriodEnd: false,
          },
        });
        setCancelModalIsOpen(false);
        enqueueSnackbar(
          "You successfully cancelled your pending subscription cancellation.",
          {
            variant: "success",
          }
        );
      },
    }
  );

  const hasScheduledDowngrade = subscriptionQuery.data
    ? !!subscriptionQuery.data.activeSubscription &&
      subscriptionQuery.data.activeSubscription.nextSub != null
    : true;

  const hasScheduledCancellation = subscriptionQuery.data
    ? !!subscriptionQuery.data.activeSubscription &&
      subscriptionQuery.data.activeSubscription.cancelAtPeriodEnd === true
    : true;

  const [isEditingOverage, setIsEditingOverage] = useState(false);
  const [newOverageBudget, setNewOverageBudget] = useState(0);

  const isLoading = subscriptionQuery.isLoading || creditsQuery.isLoading;
  const isError = subscriptionQuery.isError || creditsQuery.isError;
  const isSuccess = subscriptionQuery.isSuccess && creditsQuery.isSuccess;

  const isExternallyManaged =
    subscriptionQuery.data?.activeSubscription?.plan.externally_managed;

  const queryClient = useQueryClient();

  const [cancelModalIsOpen, setCancelModalIsOpen] = useState(false);

  const hasNoSubscription =
    subscriptionQuery.isSuccess &&
    subscriptionQuery.data?.activeSubscription == null;

  const getGradientColor = (value: number) => {
    const red = Math.round((value / 100) * 255);
    const blue = 255 - red;
    return `rgb(${red}, 0, ${blue})`; // Gradient from blue (0%) to red (100%)
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12}>
        {/* Block for 'pending cancellation' alert */}

        {subscriptionQuery.isSuccess &&
          isSuccess &&
          subscriptionQuery.data.activeSubscription &&
          hasScheduledCancellation && (
            <>
              <Alert
                severity="info"
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      futureSubscriptionMutation.reset();
                      setCancelModalIsOpen(true);
                    }}
                  >
                    Cancel scheduled cancellation
                  </Button>
                }
              >
                <AlertTitle>Scheduled plan cancellation</AlertTitle>
                You have a scheduled cancellation to your{" "}
                {
                  subscriptionQuery.data.activeSubscription.plan.display_name
                }{" "}
                subscription, that will take effect on the{" "}
                {formatTimestamp(
                  subscriptionQuery.data.activeSubscription.renewalDate * 1000
                )}
                . Until then you are still able to use all of the features of{" "}
                {toTitleCase(
                  subscriptionQuery.data.activeSubscription.plan
                    .product_display_name
                )}
                .
              </Alert>

              <ConfirmationModal
                isLoading={undoScheduledCancellationMutation.isLoading}
                isError={undoScheduledCancellationMutation.isError}
                open={cancelModalIsOpen}
                header={`Keep Your Subscription Active?`}
                body={`You previously requested to cancel your subscription. By confirming here, you are cancelling that cancellation request. This means your ${
                  subscriptionQuery.data.activeSubscription.plan.display_name
                } subscription will continue, and renew on ${formatTimestamp(
                  subscriptionQuery.data.activeSubscription.renewalDate * 1000
                )}`}
                leftButtonText={"Confirm"}
                leftButtonColor="primary"
                rightButtonText={"Cancel"}
                rightButtonColor="warning"
                handleLeftButton={() =>
                  undoScheduledCancellationMutation.mutate()
                }
                handleRightButton={() => {
                  setCancelModalIsOpen(false);
                }}
                handleClose={() => setCancelModalIsOpen(false)}
              />
            </>
          )}

        {/* Block for 'scheduled downgrade' alert */}
        {subscriptionQuery.isSuccess &&
          isSuccess &&
          subscriptionQuery.data.activeSubscription &&
          hasScheduledDowngrade && (
            <>
              <Alert
                severity="info"
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      futureSubscriptionMutation.reset();
                      setCancelModalIsOpen(true);
                    }}
                  >
                    Cancel scheduled change
                  </Button>
                }
              >
                <AlertTitle>Scheduled plan change</AlertTitle>
                You have a scheduled plan change from{" "}
                {
                  subscriptionQuery.data.activeSubscription.plan.display_name
                } to{" "}
                {
                  subscriptionQuery.data.activeSubscription.nextSub!.plan
                    .display_name
                }{" "}
                that will take effect on the{" "}
                {formatTimestamp(
                  subscriptionQuery.data.activeSubscription.nextSub!.startTime *
                    1000
                )}
                . Until then you are still able to use all of the features of{" "}
                {toTitleCase(
                  subscriptionQuery.data.activeSubscription.plan
                    .product_display_name
                )}
                .
              </Alert>

              <ConfirmationModal
                isLoading={futureSubscriptionMutation.isLoading}
                isError={futureSubscriptionMutation.isError}
                open={cancelModalIsOpen}
                header={`Confirm Cancellation of Plan Change`}
                body={`Are you sure you want to cancel the scheduled plan change? When cancelled, your previous subscription of ${
                  subscriptionQuery.data.activeSubscription.plan.display_name
                } will renew on ${formatTimestamp(
                  subscriptionQuery.data.activeSubscription.renewalDate * 1000
                )}.`}
                leftButtonText={"Confirm"}
                leftButtonColor="primary"
                rightButtonText={"Cancel"}
                rightButtonColor="warning"
                handleLeftButton={() => futureSubscriptionMutation.mutate()}
                handleRightButton={() => {
                  setCancelModalIsOpen(false);
                }}
                handleClose={() => setCancelModalIsOpen(false)}
              />
            </>
          )}
      </Grid>

      <Grid item xs={12} md={6}>
        <CardHeader title="Current Plan" />
      </Grid>
      <Grid item xs={12} md={6}>
        {/* Current Plan Card */}
        <Card>
          {isError && !hasNoSubscription && (
            <CardContent>
              <Alert variant="outlined" severity="error">
                An error occurred while fetching the subscription information.
              </Alert>
            </CardContent>
          )}
          {isLoading && (
            <CardContent>
              <Skeleton variant="rectangular" height="200px" />
            </CardContent>
          )}
          {!isLoading && hasNoSubscription && (
            <>
              <CardHeader title="None" />
              <CardContent>
                <Alert variant="outlined" severity="warning">
                  Hub is better with a sub, subscribe to get more out of Hub.
                </Alert>
              </CardContent>
            </>
          )}
          {isSuccess && subscriptionQuery.data.activeSubscription && (
            <>
              <CardHeader
                title={toTitleCase(
                  subscriptionQuery.data.activeSubscription.plan
                    .product_display_name
                )}
              />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <InputLabel>Term</InputLabel>
                    <Typography variant="body2">
                      {formatInterval(
                        subscriptionQuery.data.activeSubscription.plan.cadence
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <InputLabel>Renewal Date</InputLabel>
                    <Typography variant="body2">
                      {formatTimestamp(
                        subscriptionQuery.data.activeSubscription.renewalDate *
                          1000
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <InputLabel>Credit Usage</InputLabel>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: "100%", mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          sx={{
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: getGradientColor(
                                Math.min(
                                  (creditsQuery.data.subCreditsSpent /
                                    creditsQuery.data.subCreditsLimit) *
                                    100,
                                  100
                                )
                              ),
                            },
                          }}
                          value={Math.min(
                            (creditsQuery.data.subCreditsSpent /
                              creditsQuery.data.subCreditsLimit) *
                              100,
                            100
                          )}
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {`${humanizeNumber(
                            creditsQuery.data.subCreditsSpent
                          )}/${humanizeNumber(
                            creditsQuery.data.subCreditsLimit
                          )}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <InputLabel>Support Add-ons:</InputLabel>
                    <Typography variant="body2">-</Typography>
                  </Box>
                  {!creditsQuery.data.isOverageEnabled && (
                    <Alert severity="warning">
                      When you run out of credit, your apps will stop!
                    </Alert>
                  )}
                  {isExternallyManaged && (
                    <Alert severity="info">
                      To manage your plan please contact support
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </>
          )}

          <CardActions
            sx={{
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              disabled={
                isExternallyManaged ||
                hasScheduledDowngrade ||
                hasScheduledCancellation
              }
              onClick={() => {
                history.push(Routes.orgManageSubscription);
              }}
            >
              Edit Subscription
            </Button>
          </CardActions>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <CardHeader title="Overage" subheader="Set a maximum spend limit" />
      </Grid>
      <Grid item xs={12} md={6}>
        {/* Overage card */}
        <Card>
          <CardHeader title="Overage Limit" />
          <CardContent>
            {isError && !hasNoSubscription && (
              <Alert variant="outlined" severity="error">
                An error occurred while fetching the overage information.
              </Alert>
            )}
            {isLoading && <Skeleton variant="rectangular" height="200px" />}
            {!isLoading && hasNoSubscription && (
              <>
                <CardContent>
                  <Alert variant="outlined" severity="warning">
                    This is not available without a subscription.
                  </Alert>
                </CardContent>
              </>
            )}
            {isSuccess && subscriptionQuery.data.activeSubscription && (
              <>
                {isExternallyManaged && (
                  <Alert severity="info">
                    Your subscription is managed outside of Geoscape Hub. Please
                    refer to your contract for details on what happens when you
                    reach the end of your contracted credit balance before the
                    end of your contract period.
                  </Alert>
                )}
                {!isExternallyManaged && paymentMethodQuery.data == null && (
                  <Alert
                    severity="warning"
                    style={{ marginTop: "32px", marginBottom: "28px" }}
                    action={
                      <Button
                        color="inherit"
                        onClick={() => history.push(Routes.orgBillingDetails)}
                      >
                        Add Payment Method
                      </Button>
                    }
                  >
                    <AlertTitle>Overage can't be configured</AlertTitle>
                    Overage can't be configured without a valid payment method
                    attached to your account.
                  </Alert>
                )}
                {!isExternallyManaged && paymentMethodQuery.data !== null && (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <InputLabel>Overage Rate</InputLabel>
                      {creditsQuery.data.isOverageEnabled && (
                        <Typography variant="body2">
                          {`$${creditsQuery.data.overageDollarsPerCreditsBlock} per ${creditsQuery.data.overageCreditsBlock} credits.`}
                        </Typography>
                      )}
                      {!creditsQuery.data.isOverageEnabled && (
                        <Typography variant="body2">-</Typography>
                      )}
                    </Box>
                    {!isEditingOverage && (
                      <>
                        <Box>
                          <InputLabel>Overage Spend</InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                $
                              </Typography>
                            </Box>
                            <Box sx={{ width: "100%", mr: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                sx={{
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: getGradientColor(
                                      creditsQuery.data.isOverageEnabled
                                        ? Math.min(
                                            (creditsQuery.data
                                              .overageDollarsSpent /
                                              creditsQuery.data
                                                .overageDollarsLimit) *
                                              100,
                                            100
                                          )
                                        : 0
                                    ),
                                  },
                                }}
                                value={
                                  creditsQuery.data.isOverageEnabled
                                    ? Math.min(
                                        (creditsQuery.data.overageDollarsSpent /
                                          creditsQuery.data
                                            .overageDollarsLimit) *
                                          100,
                                        100
                                      )
                                    : 0
                                }
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                {`${formatPrice(
                                  creditsQuery.data.overageDollarsSpent
                                )}/$${creditsQuery.data.overageDollarsLimit}`}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Typography variant="subtitle1">
                          {`You have a $${creditsQuery.data.overageDollarsLimit.toLocaleString()} overage limit, set on your organisation.`}
                        </Typography>
                        {creditsQuery.data.isOverageEnabled &&
                          creditsQuery.data.overageDollarsSpent >=
                            creditsQuery.data.overageDollarsLimit && (
                            <Alert severity="error">
                              You've consumed all your set overage up to your
                              limit.
                            </Alert>
                          )}
                      </>
                    )}
                    {isEditingOverage && (
                      <OverageField
                        currentOverage={creditsQuery.data}
                        plan={subscriptionQuery.data.activeSubscription.plan}
                        onCancel={() => setIsEditingOverage(false)}
                        onSubmit={() => setIsEditingOverage(false)}
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </CardContent>
          {isSuccess &&
            !isEditingOverage &&
            paymentMethodQuery.data !== null &&
            subscriptionQuery.data.activeSubscription &&
            !isExternallyManaged && (
              <CardActions
                sx={{
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  size="small"
                  onClick={() => {
                    setIsEditingOverage(true);
                  }}
                >
                  Edit
                </Button>
              </CardActions>
            )}
        </Card>
      </Grid>
    </Grid>
  );
};
